// US-Eastern deadline math for the editor-facing countdown.
// The lateness policy is anchored to US Eastern calendar days (see backend
// _days_late_eastern). A countdown to a FIXED UTC instant is timezone-proof:
// it reads correctly even on a browser whose clock is set to the wrong zone
// (the exact situation that caused the Aldhila dispute — his browser showed
// Pacific time, making a past-Eastern-deadline submission look on time).
//
// The EST/EDT offset is ALWAYS derived from America/New_York for the actual
// date via Intl — never hardcoded — so a January (EST, UTC-5) due date and a
// June (EDT, UTC-4) due date both resolve correctly, including DST-boundary days.

// Offset (ms) such that:  easternWallClock = utcInstant + offset
function easternOffsetMs(date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0; // some engines emit '24' at midnight
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
}

// UTC ms of a given Eastern wall-clock time (defaults to 00:00:00).
// Date.UTC normalizes day overflow, so passing (y, m, d+1) is safe.
function easternWallToUtcMs(y, m, d, hh = 0, mm = 0, ss = 0) {
  const guess = Date.UTC(y, m - 1, d, hh, mm, ss);
  const offset1 = easternOffsetMs(new Date(guess));
  let utc = guess - offset1;
  const offset2 = easternOffsetMs(new Date(utc)); // re-check across a DST edge
  if (offset2 !== offset1) utc = guess - offset2;
  return utc;
}

// Deadline instant for a due-date string 'YYYY-MM-DD':
//   00:00:00 ET on (dueDate + 1 day) === end of the due day, US Eastern.
// (A due date of 2026-05-22 means the editor has all of May 22 ET; the first
//  late instant is midnight ET starting May 23.)
export function deadlineInstantMs(dueDateStr) {
  if (!dueDateStr) return null;
  const [y, m, d] = dueDateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  return easternWallToUtcMs(y, m, d + 1, 0, 0, 0);
}

// Eastern calendar-day lateness — mirrors backend _days_late_eastern exactly.
export function easternDaysLate(dueDateStr, nowMs = Date.now()) {
  if (!dueDateStr) return 0;
  const nowEastDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(nowMs)); // 'YYYY-MM-DD' (lexically comparable)
  if (nowEastDate <= dueDateStr) return 0;
  const [y1, m1, d1] = dueDateStr.split('-').map(Number);
  const [y2, m2, d2] = nowEastDate.split('-').map(Number);
  const diff = (Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000;
  return Math.max(0, Math.round(diff));
}

// Returns { overdue, text, daysLate, soon } or null for a missing/invalid date.
//   not overdue -> "Due in 2d 6h" / "Due in 6h" / "Due in 12m"  (soon = within 24h)
//   overdue     -> "Overdue by 1d" (daysLate from the Eastern calendar rule)
export function deadlineCountdown(dueDateStr, nowMs = Date.now()) {
  const target = deadlineInstantMs(dueDateStr);
  if (target == null) return null;
  const remMs = target - nowMs;
  if (remMs > 0) {
    const totalMin = Math.floor(remMs / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    let text;
    if (days > 0) text = `Due in ${days}d ${hours}h`;
    else if (hours > 0) text = `Due in ${hours}h`;
    else text = `Due in ${Math.max(1, totalMin)}m`;
    return { overdue: false, text, daysLate: 0, soon: remMs <= 24 * 3600 * 1000 };
  }
  const daysLate = easternDaysLate(dueDateStr, nowMs);
  return { overdue: true, text: daysLate >= 1 ? `Overdue by ${daysLate}d` : 'Overdue', daysLate, soon: false };
}

// Countdown to an ISO instant (e.g. project.revision_due_at, request + 24h).
//   not overdue -> "Revision due in 14h" / "Revision due in 2d 3h" / "Revision due in 12m"
//   overdue     -> "Revision overdue by 3h" / "Revision overdue by 2d"
// The clock the bonus scores stops when the editor's UPLOAD STARTS (see
// backend app/revision_response.py), so the label says so on hover.
export function instantCountdown(iso, nowMs = Date.now(), label = 'Revision') {
  if (!iso) return null;
  const target = Date.parse(iso);
  if (Number.isNaN(target)) return null;
  const remMs = target - nowMs;
  const fmt = (ms) => {
    const totalMin = Math.floor(Math.abs(ms) / 60000);
    const days = Math.floor(totalMin / 1440);
    const hours = Math.floor((totalMin % 1440) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return `${Math.max(1, totalMin)}m`;
  };
  if (remMs > 0) return { overdue: false, text: `${label} due in ${fmt(remMs)}`, soon: remMs <= 6 * 3600 * 1000 };
  return { overdue: true, text: `${label} overdue by ${fmt(remMs)}`, soon: false };
}
